<template>
  <div>
    <template v-if="createWorkspaceInProgress">
      <div class="h-100 w-100 d-flex align-center justify-center">
        <div class="progress-container d-flex flex-column align-center text-center">
          <v-progress-circular color="primary" indeterminate size="48" class="mb-5 mt-12" />
          <h5 class="headline5 text-center px-6">
            {{ $t("modals.createWorkspace.requestInProgress") }}
          </h5>
        </div>
      </div>
    </template>
    <div
      v-else-if="createWorkspaceCompleted"
      class="h-100 w-100 d-flex align-center justify-center"
    >
      <div class="progress-container d-flex flex-column align-center text-center">
        <v-icon size="80" class="mb-6" icon="$noIssues" />
        <h5 class="headline5 mr-12 ml-12 text-center">
          {{
            $t("modals.createWorkspace.workspaceCreated", {
              workspace: localValue.item.displayName,
            })
          }}
        </h5>
        <div class="body2 w-60 mt-2 mb-6 mr-12 ml-12">
          {{
            adminExistsInOtherHierarchy
              ? $t("modals.createWorkspace.mspAdminExists", {
                  email: localValue.item.adminEmail,
                })
              : $t("modals.createWorkspace.inviteSent", {
                  email: localValue.item.adminEmail,
                })
          }}
        </div>
        <v-btn size="large" rounded color="primary" @click="$emit('dismissed')">
          {{ $t("general.done") }}
        </v-btn>
      </div>
    </div>
    <div v-else-if="createWorkspaceFailed" class="h-100 w-100 d-flex align-center justify-center">
      <div class="progress-container d-flex flex-column align-center">
        <v-icon size="80" class="mb-6" icon="$warning" />
        <h5 class="headline5 mb-6 mr-12 ml-12 text-center">
          {{ $t("modals.createWorkspace.requestFailed") }}
        </h5>
        <v-btn size="large" rounded color="primary" @click="$emit('dismissed')">
          {{ $t("general.ok") }}
        </v-btn>
      </div>
    </div>
    <v-form ref="form" v-else v-model="valid" @submit.prevent>
      <v-stepper v-model="currentStep" width="100vw">
        <template v-slot:default>
          <div class="d-flex justify-center w-100 scroll-area">
            <div class="dialog-content">
              <v-stepper-header>
                <div class="mt-6 progress-bar__content">
                  <div class="body3 mb-4">
                    {{ $t("general.step") }}
                    {{ getCurrentStepNumber(steps) }}
                    {{ $t("general.of") }}
                    {{ steps.length }}
                  </div>
                  <v-progress-linear color="#46A762" v-model="stepsProgress"></v-progress-linear>
                </div>
                <template v-for="n in steps" :key="`${n}-step`">
                  <v-stepper-item
                    class="d-none"
                    :complete="currentStep > n"
                    :step="`Step {{ n }}`"
                    :value="n"
                    editable
                  ></v-stepper-item>
                </template>
              </v-stepper-header>
              <v-stepper-window :key="currentStep">
                <v-stepper-window-item v-for="step in steps" :key="`${step}-content`" :value="step">
                  <div class="stepper-step__wrapper">
                    <div>
                      <template v-if="step === workspaceCreationSteps.COMPANY_DETAILS">
                        <div class="headline5 mb-3">
                          {{ $t("modals.createWorkspace.companyDetails") }}
                        </div>
                        <v-card>
                          <v-card-title>
                            <v-row>
                              <v-col class="mt-2 mb-2" :cols="8">
                                <v-text-field
                                  v-model="localValue.item.companyName"
                                  type="text"
                                  data-testid="add-workspace-modal-company-name"
                                  :rules="companyNameRules"
                                  variant="outlined"
                                  :label="$t('modals.createWorkspace.companyName')"
                                  :aria-label="$t('modals.createWorkspace.companyName')"
                                  :placeholder="$t('modals.createWorkspace.companyName')"
                                ></v-text-field>
                                <v-text-field
                                  v-model="localValue.item.displayName"
                                  type="text"
                                  data-testid="add-workspace-modal-display-name"
                                  class="mt-4"
                                  :rules="displayNameRules"
                                  counter="30"
                                  :label="$t('modals.createWorkspace.displayName')"
                                  :aria-label="$t('modals.createWorkspace.displayName')"
                                  :placeholder="$t('modals.createWorkspace.displayName')"
                                  :hint="$t('modals.createWorkspace.displayNameHint')"
                                  persistent-hint
                                  variant="outlined"
                                ></v-text-field>
                                <v-text-field
                                  v-model="localValue.item.domain"
                                  type="text"
                                  data-testid="add-workspace-modal-domain"
                                  class="mt-4"
                                  :rules="domainRules"
                                  :placeholder="$t('modals.createWorkspace.domainPlaceholder')"
                                  :label="$t('modals.createWorkspace.domain')"
                                  :aria-label="$t('modals.createWorkspace.domain')"
                                  :hint="$t('modals.createWorkspace.domainHint')"
                                  persistent-hint
                                  variant="outlined"
                                >
                                </v-text-field>
                                <v-autocomplete
                                  v-if="showParentWorkspaceIdInput"
                                  v-model="localValue.item.parentWorkspaceId"
                                  class="mt-4"
                                  :placeholder="$t('modals.createWorkspace.parentWorkspace')"
                                  :label="$t('modals.createWorkspace.parentWorkspace')"
                                  item-title="workspaceName"
                                  item-value="workspaceId"
                                  :hide-no-data="false"
                                  :no-filter="true"
                                  auto-select-first="exact"
                                  :rules="parentWorkspaceIdRules"
                                  :loading="parentWorkspaceInputLoading"
                                  :menu-props="{
                                    maxHeight: '250',
                                  }"
                                  :items="parentWorkspaces.items"
                                  variant="outlined"
                                  @update:search="onParentWorkspaceSearchUpdate($event)"
                                >
                                  <template #item="{ item, props }">
                                    <v-list-item v-bind="props" title="">
                                      <v-list-item-title title="">
                                        {{ `${item.raw.workspaceName} (${item.raw.workspaceId})` }}
                                      </v-list-item-title>
                                    </v-list-item>
                                  </template>
                                  <template #append-item>
                                    <div v-intersect="onParentWorkspaceIntersect" />
                                  </template>
                                </v-autocomplete>
                                <v-text-field
                                  v-model="localValue.item.adminEmail"
                                  class="mt-4"
                                  type="text"
                                  data-testid="add-workspace-modal-admin-email"
                                  :rules="adminEmailRules"
                                  :label="$t('modals.createWorkspace.adminEmail')"
                                  :aria-label="$t('modals.createWorkspace.adminEmail')"
                                  :placeholder="$t('modals.createWorkspace.adminEmailPlaceholder')"
                                  :hint="$t('modals.createWorkspace.adminEmailHint')"
                                  persistent-hint
                                  variant="outlined"
                                ></v-text-field>
                                <template v-if="isChildWorkspace || isRegularWorkspace">
                                  <div class="subtitle2 mt-4 mb-2">
                                    {{ $t("modals.createWorkspace.userAndDeviceLimits") }}
                                  </div>
                                  <workspace-limits-card
                                    v-model:limited="localValue.item.limited"
                                    v-model:limits="localValue.item.limits"
                                    inline
                                  />
                                </template>
                              </v-col>
                            </v-row>
                          </v-card-title>
                        </v-card>
                      </template>
                      <template v-if="step === workspaceCreationSteps.MSP_SETTINGS">
                        <div class="headline5 mb-3">
                          {{ $t("modals.createWorkspace.workspaceSettings") }}
                        </div>
                        <v-card>
                          <v-card-text>
                            <v-select
                              v-model="localValue.item.languageCode"
                              :items="Object.values(WorkspaceLocale)"
                              class="mt-4"
                              :rules="[required()]"
                              :label="$t('modals.createWorkspace.language.label')"
                              :placeholder="$t('modals.createWorkspace.language.placeholder')"
                              persistent-hint
                              variant="outlined"
                            >
                              <template #selection="{ item }">
                                <span class="body2">
                                  {{ $t(`general.localeDisplayNames.${item.raw}`) }}
                                </span>
                              </template>
                              <template #item="{ item, props }">
                                <v-list-item
                                  v-bind="props"
                                  :title="$t(`general.localeDisplayNames.${item.raw}`)"
                                >
                                </v-list-item>
                              </template>
                            </v-select>

                            <v-autocomplete
                              v-if="showLabelsField"
                              v-model="localValue.item.labels"
                              class="mt-4"
                              :placeholder="$t('modals.editWorkspace.selectLabel')"
                              :label="$t('modals.editWorkspace.selectLabel')"
                              :hide-no-data="false"
                              :no-filter="true"
                              :menu-props="{
                                maxHeight: '250',
                              }"
                              :items="labels"
                              variant="outlined"
                              multiple
                              return-object
                              item-value="id"
                              item-title="name"
                              @update:search="onLabelSearch($event)"
                            >
                              <template v-slot:chip="{ props, item }">
                                <v-chip
                                  v-bind="props"
                                  :closable="true"
                                  variant="flat"
                                  size="default"
                                  :color="item.raw.color"
                                  close-icon="$closeCircle"
                                  :text="item.title"
                                  label
                                  rounded="3"
                                >
                                  <span class="coro-label text-uppercase text-primary">
                                    {{ item.title }}
                                  </span>
                                </v-chip>
                              </template>
                              <template #item="{ item, props }">
                                <v-list-item v-bind="props" title="">
                                  <v-list-item-title title="">
                                    <div class="d-flex align-center label-popup-block">
                                      <div
                                        class="color-preview mr-2"
                                        :style="{ 'background-color': item.raw.color }"
                                      />
                                      <span>
                                        {{ item.raw.name }}
                                      </span>
                                    </div>
                                  </v-list-item-title>
                                </v-list-item>
                              </template>
                            </v-autocomplete>

                            <template v-if="isChildWorkspace">
                              <div class="subtitle1 mb-4">
                                {{ $t("modals.createWorkspace.globalProtectionPoliciesTitle") }}
                              </div>
                              <v-select
                                v-model="localValue.item.mspPolicyId"
                                :items="globalPolicies.items"
                                data-testid="global-policy-select"
                                class="mb-4"
                                item-value="id"
                                item-title="name"
                                :rules="[required()]"
                                :label="$t('modals.createWorkspace.mspPolicyIdLabel')"
                                :placeholder="$t('modals.createWorkspace.mspPolicyIdLabel')"
                                variant="outlined"
                                hide-details="auto"
                                auto-select-first="exact"
                                :loading="globalPoliciesLoading"
                              >
                                <template #append-item>
                                  <div v-intersect="onWorkspacePolicyIntersect" />
                                </template>
                              </v-select>

                              <v-switch
                                v-model="localValue.item.allowPolicyOverriding"
                                :ripple="false"
                                density="compact"
                                hide-details
                                color="primary"
                                inset
                                flat
                                data-testid="allow-local-overriding-switch"
                              >
                                <template #label>
                                  <div class="body2">
                                    {{ $t("modals.createWorkspace.allowLocalOverriding") }}
                                  </div>
                                </template>
                              </v-switch>
                            </template>

                            <billing-fields
                              v-if="isGlobalContext"
                              v-model="localValue.item"
                              :workspace-type="localValue.item.type"
                              :parent-workspace-id="localValue.item.parentWorkspaceId"
                              test-id-prefix="create-workspace"
                            />

                            <div class="subtitle1 mt-4 mb-4">
                              {{ $t("modals.createWorkspace.notifications") }}
                            </div>
                            <v-radio-group
                              v-model="localValue.item.notificationsLevel"
                              hide-details="auto"
                            >
                              <v-radio
                                v-for="notificationLevel in notificationLevels"
                                class="mb-2"
                                :key="notificationLevel"
                                :value="notificationLevel"
                                :data-testid="`add-workspace-modal-${notificationLevel}-radio`"
                              >
                                <template #label>
                                  <span
                                    class="body1 notification-label"
                                    v-html="
                                      getNotificationLevelText(
                                        localValue.item.type,
                                        notificationLevel,
                                        domainDisplayName
                                      )
                                    "
                                  ></span>
                                </template>
                              </v-radio>
                            </v-radio-group>
                            <div class="subtitle1 mt-4 mb-4">
                              {{ $t("modals.createWorkspace.advancedSettings") }}
                            </div>
                            <v-switch
                              v-if="isChannelWorkspace"
                              v-model="localValue.item.supportEnabled"
                              class="mb-2"
                              :ripple="false"
                              density="compact"
                              hide-details
                              color="primary"
                              inset
                              flat
                            >
                              <template #label>
                                <div class="body2">
                                  {{ $t("modals.createWorkspace.showSupportLink") }}
                                </div>
                              </template>
                            </v-switch>
                            <v-switch
                              v-if="showScanUnprotectedUsers"
                              v-model="localValue.item.scanUnprotectedUsers"
                              class="mb-2"
                              :ripple="false"
                              density="compact"
                              hide-details
                              color="primary"
                              inset
                              flat
                            >
                              <template #label>
                                <div class="body2">
                                  {{ $t("modals.createWorkspace.scanUnprotectedUsers") }}
                                </div>
                              </template>
                            </v-switch>
                            <v-switch
                              v-model="localValue.item.showDisabledModules"
                              class="mb-2"
                              :ripple="false"
                              density="compact"
                              hide-details
                              color="primary"
                              inset
                              flat
                            >
                              <template #label>
                                <div class="body2">
                                  {{ $t("modals.createWorkspace.showDisabledModules") }}
                                </div>
                              </template>
                            </v-switch>
                            <v-switch
                              v-if="isGlobalAdmin"
                              v-model="localValue.item.isCoroEmployeeWorkspace"
                              class="mb-2"
                              :ripple="false"
                              density="compact"
                              hide-details
                              color="primary"
                              inset
                              flat
                            >
                              <template #label>
                                <div class="body2">
                                  {{ $t("modals.createWorkspace.coroEmployeeWorkspace") }}
                                </div>
                              </template>
                            </v-switch>
                          </v-card-text>
                        </v-card>
                      </template>
                      <template v-if="step === workspaceCreationSteps.BRANDING">
                        <div class="headline5 mb-3">
                          {{ $t("modals.createWorkspace.brandingSettings") }}
                        </div>
                        <div class="mt-3 mb-4 px-3 py-1 info-block">
                          <v-icon size="24" icon="$info" />
                          <span class="body2 ml-2">
                            {{ $t("modals.editWorkspace.branding.infoBlock") }}
                          </span>
                        </div>
                        <div class="d-flex justify-space-between w-100">
                          <v-card height="610px" width="818px" class="mr-5">
                            <v-card-text class="pb-4 pt-4">
                              <v-text-field
                                v-model="localValue.item.branding.brandColor"
                                type="text"
                                data-testid="edit-workspace-modal-color-input"
                                class="mt-3"
                                :rules="brandColorRules"
                                :label="$t('modals.createWorkspace.branding.brandColorLabel')"
                                :aria-label="$t('modals.createWorkspace.branding.brandColorLabel')"
                                :placeholder="$t('modals.createWorkspace.branding.brandColorLabel')"
                                :hint="$t('modals.createWorkspace.branding.brandColorHint')"
                                persistent-hint
                                variant="outlined"
                                @update:model-value="
                                  localValue.item.branding.brandColor = $event?.toUpperCase()
                                "
                              >
                                <template v-slot:append-inner>
                                  <div
                                    class="color-preview"
                                    :style="{
                                      'background-color': localValue.item.branding.brandColor,
                                    }"
                                  ></div>
                                </template>
                              </v-text-field>
                              <v-text-field
                                v-model="localValue.item.branding.alias"
                                type="text"
                                data-testid="edit-workspace-modal-alias"
                                class="mt-3"
                                counter="40"
                                :rules="aliasRules"
                                :label="$t('modals.createWorkspace.branding.aliasLabel')"
                                :aria-label="$t('modals.createWorkspace.branding.aliasLabel')"
                                :placeholder="$t('modals.createWorkspace.branding.aliasLabel')"
                                :hint="aliasHint"
                                persistent-hint
                                :disabled="!isGlobalAdmin"
                                variant="outlined"
                              ></v-text-field>
                              <v-text-field
                                v-model="localValue.item.branding.noReplyEmail"
                                type="text"
                                data-testid="edit-workspace-modal-no-reply-email"
                                class="mt-3"
                                readonly
                                :label="$t('modals.createWorkspace.branding.noReplyEmailLabel')"
                                :aria-label="
                                  $t('modals.createWorkspace.branding.noReplyEmailLabel')
                                "
                                :placeholder="
                                  $t('modals.createWorkspace.branding.noReplyEmailLabel')
                                "
                                :hint="$t('modals.createWorkspace.branding.noReplyEmailHint')"
                                persistent-hint
                                variant="outlined"
                              ></v-text-field>
                              <v-text-field
                                v-model="localValue.item.branding.supportEmail"
                                type="text"
                                data-testid="create-workspace-modal-support-email"
                                class="mt-3"
                                counter="100"
                                :rules="supportEmailRules"
                                :label="$t('modals.createWorkspace.branding.supportEmailLabel')"
                                :aria-label="
                                  $t('modals.createWorkspace.branding.supportEmailLabel')
                                "
                                :placeholder="
                                  $t('modals.createWorkspace.branding.supportEmailLabel')
                                "
                                :hint="$t('modals.createWorkspace.branding.supportEmailHint')"
                                persistent-hint
                                variant="outlined"
                              ></v-text-field>
                              <v-textarea
                                v-model="localValue.item.branding.companyAddress"
                                type="text"
                                data-testid="create-workspace-modal-company-address"
                                class="mt-3"
                                :label="$t('modals.editWorkspace.branding.companyAddressLabel')"
                                :placeholder="
                                  $t('modals.editWorkspace.branding.companyAddressLabel')
                                "
                                :hint="$t('modals.editWorkspace.branding.companyAddressHint')"
                                persistent-hint
                                variant="outlined"
                              ></v-textarea>
                            </v-card-text>
                          </v-card>

                          <v-card width="306px" height="610px">
                            <v-card-text>
                              <div class="subtitle1 mt-2">
                                {{ $t("modals.createWorkspace.replaceCoroIcons") }}
                              </div>
                              <div class="d-flex flex-column align-center justify-center mt-4 mb-4">
                                <logo-uploader
                                  class="mb-5"
                                  :brand-color="localValue.item.branding.brandColor"
                                  :default-logo="localValue.item.branding.headerIconUrl"
                                  @logo-changed="handleImageChange($event)"
                                ></logo-uploader>
                                <logo-uploader
                                  color="primary"
                                  brand-color="#eeeef3"
                                  :title="$t('logoUploader.emailIcon')"
                                  :hint="$t('logoUploader.emailImageHint')"
                                  :description="$t('logoUploader.emailDescription')"
                                  :default-logo="
                                    localValue.item.branding.emailIconUrl || defaultLogoUrl.PRIMARY
                                  "
                                  @logo-changed="handleEmailImageChange($event)"
                                ></logo-uploader>
                              </div>
                            </v-card-text>
                          </v-card>
                        </div>
                      </template>
                      <template v-if="step === workspaceCreationSteps.SUBSCRIPTION_RULES">
                        <div class="headline5 mb-3">
                          {{ $t("modals.createWorkspace.subscriptionRules") }}
                        </div>
                        <cui-subscription-rules-card
                          class="py-6"
                          :hidden-modules="hiddenModules"
                          prune-hidden-modules
                          v-model:subscription-config="localValue.item.subscriptionRules"
                          :bundle-or-module-is-required-message="
                            $t('validations.bundleOrModuleIsRequired')
                          "
                          :validation-aria-label="$t('ariaLabels.subscriptionRulesValidation')"
                          :allowed-bundles-label="$t('subscriptions.allowedBundles')"
                          :choose-available-bundles-label="
                            $t('subscriptions.chooseAvailableBundles')
                          "
                          :new-bundles-label="$t('subscriptions.newBundles')"
                          :legacy-bundles-label="$t('subscriptions.legacyBundles')"
                          :allowed-modules-label="$t('subscriptions.allowedModules')"
                          :choose-allowed-modules-label="$t('subscriptions.chooseAllowedModules')"
                          :bundle-labels="bundleLabels"
                          :module-labels="moduleLabels"
                          :please-select-at-least-one-option-message="
                            $t('validations.bundleOrModuleIsRequired')
                          "
                          :parent-workspace-subscription-config="parentSubscriptionRules"
                        />
                      </template>

                      <template v-if="step === workspaceCreationSteps.SUBSCRIPTION">
                        <div class="headline5 mb-3">
                          {{ $t("modals.createWorkspace.chooseYourSubscription") }}
                        </div>
                        <update-subscription-card
                          v-model:subscription="localValue.item.subscription"
                          :subscription-rules="localValue.item.subscriptionRules"
                          :is-form-valid="valid"
                          single-bundle-selection
                        />
                      </template>

                      <template v-if="step === workspaceCreationSteps.REVIEW_SUBSCRIPTION">
                        <div class="headline5 mb-3">
                          {{ $t("modals.createWorkspace.reviewSubscription") }}
                        </div>
                        <div class="d-flex w-100">
                          <v-card width="100%">
                            <v-card-text>
                              <div class="subtitle1 mb-3">
                                {{ $t("modals.createWorkspace.summary.title") }}
                              </div>
                              <div class="w-100">
                                <template v-if="localValue.item.subscription.bundles.length > 0">
                                  <div class="caption mb-3">
                                    {{ $t("modals.createWorkspace.summary.bundles") }}
                                  </div>

                                  <template
                                    v-for="bundle in localValue.item.subscription.bundles"
                                    :key="bundle"
                                  >
                                    <cui-subscription-summary-item
                                      :item="bundle"
                                      :type="subscriptionSummaryType.BUNDLE"
                                      :title="$t(`modals.createWorkspace.summary.${bundle}.title`)"
                                      :subtitle="
                                        $t(`modals.createWorkspace.summary.${bundle}.subtitle`)
                                      "
                                      :show-sat="!isCanadaEnv"
                                      class="mb-3"
                                    >
                                      <template #moduleName="{ value }">
                                        {{ $t(`subscriptions.modules.${value}`) }}
                                        <span
                                          v-if="
                                            hasSocAddonInModule(
                                              localValue.item.subscription.modules[value]
                                            )
                                          "
                                        >
                                          - {{ $t("moduleOrAddonSelector.managed") }}
                                        </span>
                                      </template>
                                      <template #addonName="{ value }">
                                        {{ $t(`subscriptions.addons.${value}`) }}
                                      </template>
                                    </cui-subscription-summary-item>
                                  </template>
                                </template>

                                <template v-if="showSummaryModulesAndAddons">
                                  <div class="caption mt-3 mb-3">
                                    {{ $t("modals.createWorkspace.summary.modulesAndAddons") }}
                                  </div>

                                  <template
                                    v-for="(module, moduleName) in localValue.item.subscription
                                      .modules"
                                    :key="moduleName"
                                  >
                                    <cui-subscription-summary-item
                                      v-if="!moduleIncludedInBundle(moduleName) && module.enabled"
                                      :item="moduleName"
                                      :type="subscriptionSummaryType.MODULE"
                                      :title="
                                        $t(`modals.createWorkspace.summary.${moduleName}.title`)
                                      "
                                      :subtitle="
                                        hasSocAddonInModule(module)
                                          ? $t('modals.createWorkspace.managed')
                                          : $t('modals.createWorkspace.unmanaged')
                                      "
                                      class="mb-3"
                                    />
                                    <template v-for="addon in module.addons" :key="addon.name">
                                      <cui-subscription-summary-item
                                        v-if="
                                          !isSocAddon(addon.name) &&
                                          addon.enabled &&
                                          !addonIncludedInBundle(addon.name)
                                        "
                                        :item="addon.name"
                                        :type="subscriptionSummaryType.ADDON"
                                        :title="
                                          $t(`modals.createWorkspace.summary.${addon.name}.title`)
                                        "
                                        :subtitle="''"
                                        class="mb-3"
                                      />
                                    </template>
                                  </template>
                                </template>
                              </div>
                            </v-card-text>
                          </v-card>
                        </div>
                      </template>
                    </div>
                  </div>
                </v-stepper-window-item>
              </v-stepper-window>
            </div>
          </div>
          <v-divider class="mb-4"></v-divider>
        </template>
        <template v-slot:actions="{ prev, next }">
          <div class="d-flex align-center justify-center w-100">
            <div class="d-flex justify-end align-center pt-1 dialog-content">
              <div>
                <v-btn
                  variant="text"
                  color="primary"
                  size="large"
                  @click="onPrevStepClick(prev, currentStep)"
                  v-if="showPreviousStepBtn"
                >
                  {{ $t("general.back") }}
                </v-btn>
                <v-btn
                  rounded
                  v-if="!showCompleteBtn"
                  color="primary"
                  size="large"
                  class="ml-2"
                  :disabled="disable"
                  @click="onNextStepClick(next)"
                >
                  {{ $t("general.next") }}
                </v-btn>
                <v-btn
                  rounded
                  v-else
                  color="primary"
                  size="large"
                  class="ml-2"
                  :disabled="disable"
                  :loading="createWorkspaceClicked"
                  @click="createWorkspace()"
                >
                  {{ $t(`modals.${config.action}.actionBtn`) }}
                </v-btn>
              </div>
            </div>
          </div>
        </template>
      </v-stepper>
    </v-form>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref, watch } from "vue";
import LogoUploader, { defaultLogoUrl } from "@/components/LogoUploader.vue";
import WorkspaceLimitsCard from "@/components/msp/WorkspaceLimitsCard.vue";
import { useMspWorkspaceModal, WorkspaceCreationSteps } from "@/composables/useMspWorkspaceModal";
import { useInfiniteScroll } from "@/composables/useInfiniteScroll";
import cloneDeep from "lodash/cloneDeep";
import {
  type MspWorkspaceDialogItem,
  NotificationsLevel,
  type ParentWorkspaceSearchPermission,
  type SubscriptionRulesConfig,
  useMspStore,
} from "@/_store/msp/msp.module.ts";
import {
  defaultSubscriptionModulesConfig,
  MspWorkspaceAction,
  SubscriptionAddon,
  SubscriptionBundle,
  SubscriptionModule,
  WorkspaceLocale,
  WorkspaceType,
} from "@/constants/workplaces";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { storeToRefs } from "pinia";
import { maxItems, required } from "@/_helpers/validators";
import debounce from "lodash/debounce";
import { isGlobalActionRestricted } from "@/_helpers/global-permissions";
import { type MspLabel, useMspLabelsStore } from "@/_store/msp/msp-labels.module.ts";
import uniqBy from "lodash/uniqBy";
import type { Paged } from "@/types.ts";
import {
  CuiSubscriptionSummaryItem,
  handleVirtualScrollData,
  hasSocAddonInModule,
  isAddonIncludedInBundle,
  isModuleIncludedInBundle,
  isSocAddon,
  SubscriptionSummaryType,
} from "@coronet/cui";
import { useGlobalWorkspacesStore } from "@/_store/global-workspaces.module.ts";
import { usePermissionsStore } from "@/_store/permissions.module.ts";
import { type SubscriptionModuleInfo, useWorkspaceStore } from "@/_store/workspace.module.ts";
import {
  type GlobalPolicyListItem,
  useMspGlobalPoliciesStore,
} from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import { ManageWorkspacesContext } from "@/constants/msp.ts";
import BillingFields from "@/components/modals/msp/BillingFields.vue";
import UpdateSubscriptionCard from "@/components/msp/UpdateSubscriptionCard.vue";
import { CuiSubscriptionRulesCard } from "@coronet/cui";
import { isCanadaEnv } from "@/constants/general.ts";
import { MspPortalScopeSection, MspRolePermissionScopes } from "@/constants/roles.ts";

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<MspWorkspaceDialogItem>>,
      required: true,
    },
    disable: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    CuiSubscriptionRulesCard,
    UpdateSubscriptionCard,
    LogoUploader,
    CuiSubscriptionSummaryItem,
    WorkspaceLimitsCard,
    BillingFields,
  },
  emits: ["dismissed", "update:localValue"],
  setup(props, { emit }) {
    const parentWorkspaceInputLoading = ref(false);
    const labelsStore = useMspLabelsStore();
    const parentWorkspaces = ref<Paged<{ workspaceName: string; workspaceId: string }>>({
      items: [],
      total: 0,
    });
    const parentWorkspaceSearchPage = ref(0);
    const parentWorkspaceSearchValue = ref("");
    const hasMoreWorkspaces = ref(true);
    const globalPolicies = ref<Paged<GlobalPolicyListItem>>({
      items: [],
      total: 0,
    });
    const globalPoliciesLoading = ref(false);
    const globalPoliciesSearchPage = ref(0);
    const hasMorePolicies = ref(true);
    const valid = ref(true);
    const workspaceStore = useWorkspaceStore();
    const permissionsStore = usePermissionsStore();
    const mspGlobalPoliciesStore = useMspGlobalPoliciesStore();
    const { isGlobalAdmin } = storeToRefs(permissionsStore);
    const adminExistsInOtherHierarchy = ref(false);
    const labels = ref<MspLabel[]>([] as MspLabel[]);
    const mspStore =
      props.config.item.context === ManageWorkspacesContext.GLOBAL_WORKSPACES
        ? useGlobalWorkspacesStore()
        : useMspStore();
    const { createWorkspaceInProgress, createWorkspaceFailed, createWorkspaceCompleted } =
      storeToRefs(mspStore);
    const createWorkspaceClicked = ref(false);
    const form = ref();
    const parentSubscriptionRules = ref<SubscriptionRulesConfig | undefined>(undefined);
    const type = computed(() => {
      switch (props.config.action) {
        case MspWorkspaceAction.CREATE_REGULAR_WORKSPACE:
          return WorkspaceType.REGULAR;
        case MspWorkspaceAction.CREATE_CHILD_WORKSPACE:
          return WorkspaceType.CHILD;
        case MspWorkspaceAction.CREATE_CHANNEL_WORKSPACE:
          return WorkspaceType.CHANNEL;
        default:
          return null;
      }
    });

    const showSubscriptionRulesStep = computed(() => {
      const isChannel = type.value === WorkspaceType.CHANNEL;
      const isRegular = type.value === WorkspaceType.REGULAR;
      if (props.config.item.context === ManageWorkspacesContext.GLOBAL_WORKSPACES) {
        return isChannel || isRegular;
      }
      return isChannel;
    });

    const defaultSubscriptionRules: SubscriptionRulesConfig =
      type.value === WorkspaceType.REGULAR
        ? {
            allowedBundles: [
              SubscriptionBundle.CORO_AI_ENDPOINT,
              SubscriptionBundle.CORO_AI_ESSENTIALS,
              SubscriptionBundle.CORO_AI_COMPLETE,
              SubscriptionBundle.CORO_AI_LITE,
            ],
            allowedModules: [],
          }
        : {
            allowedBundles: [],
            allowedModules: [],
          };

    const localValue = ref<DialogDataConfig<MspWorkspaceDialogItem>>(
      cloneDeep({
        ...props.config,
        item: {
          companyName: null,
          displayName: null,
          domain: null,
          adminEmail: null,
          notificationsLevel: NotificationsLevel.ALL,
          type: type.value,
          supportEnabled: true,
          scanUnprotectedUsers: true,
          psaEnabled: true,
          showDisabledModules: true,
          isCoroEmployeeWorkspace: false,
          languageCode: WorkspaceLocale.EN_US,
          branding: {},
          subscription: {
            bundles: [],
            modules: cloneDeep(defaultSubscriptionModulesConfig),
          },
          subscriptionRules: cloneDeep(defaultSubscriptionRules),
          labels: [],
          limited: false,
        },
      }) as unknown as DialogDataConfig<MspWorkspaceDialogItem>
    );

    const {
      limitProtectedUsersRules,
      domainRules,
      companyNameRules,
      displayNameRules,
      brandColorRules,
      adminEmailRules,
      supportEmailRules,
      aliasRules,
      filterKey,
      aliasHint,
      notificationLevels,
      currentStep,
      showPreviousStepBtn,
      showCompleteBtn,
      getCurrentStepNumber,
      getStepsProgress,
      getNotificationLevelText,
      bundleLabels,
      moduleLabels,
    } = useMspWorkspaceModal(localValue);

    const showLabelsField = computed(() => {
      return (
        props.config.item.context === ManageWorkspacesContext.SINGLE_HIERARCHY &&
        localValue.value.item.type === WorkspaceType.CHILD
      );
    });

    const steps = computed(() => {
      const allSteps = [
        WorkspaceCreationSteps.COMPANY_DETAILS,
        WorkspaceCreationSteps.MSP_SETTINGS,
        WorkspaceCreationSteps.SUBSCRIPTION,
        WorkspaceCreationSteps.REVIEW_SUBSCRIPTION,
      ];

      if (isChannelWorkspace.value) {
        const mspIndex = allSteps.indexOf(WorkspaceCreationSteps.MSP_SETTINGS);
        if (mspIndex !== -1) {
          allSteps.splice(mspIndex + 1, 0, WorkspaceCreationSteps.BRANDING);
        }
      }

      if (showSubscriptionRulesStep.value) {
        const subscriptionIndex = allSteps.indexOf(WorkspaceCreationSteps.SUBSCRIPTION);
        if (subscriptionIndex !== -1) {
          allSteps.splice(subscriptionIndex, 0, WorkspaceCreationSteps.SUBSCRIPTION_RULES);
        }
      }

      return allSteps;
    });

    const moduleIncludedInBundle = (moduleName: SubscriptionModule) => {
      return isModuleIncludedInBundle(moduleName, localValue.value.item.subscription);
    };

    const addonIncludedInBundle = (addonName: SubscriptionAddon) => {
      return isAddonIncludedInBundle(addonName, localValue.value.item.subscription);
    };

    const showParentWorkspaceIdInput = computed(() => {
      return type.value !== WorkspaceType.REGULAR;
    });

    const showSummaryModulesAndAddons = computed(() => {
      const modules = localValue.value.item.subscription.modules;
      return (
        (Object.keys(modules) as SubscriptionModule[]).filter((module) => {
          return !moduleIncludedInBundle(module) && modules[module].enabled;
        }).length > 0
      );
    });

    const showImageUploader = computed(() => {
      return props.config.action === MspWorkspaceAction.CREATE_CHANNEL_WORKSPACE;
    });

    const atLeastOneModuleNotSelected = computed(() => {
      const modulesArray = Object.values(localValue.value.item.subscription.modules);
      return modulesArray.some((module: SubscriptionModuleInfo) => !module.enabled);
    });

    const isChannelWorkspace = computed(
      () => props.config.action === MspWorkspaceAction.CREATE_CHANNEL_WORKSPACE
    );

    const isChildWorkspace = computed(
      () => props.config.action === MspWorkspaceAction.CREATE_CHILD_WORKSPACE
    );

    const isRegularWorkspace = computed(() => !isChannelWorkspace.value && !isChildWorkspace.value);

    // Cloud Backup cannot work on a Regular workspace — Keepit accounts only exist for
    // Channel/Child. SAT is not sold in Canada. Both are exclusions of the same kind.
    const hiddenModules = computed<SubscriptionModule[]>(() => [
      ...(isRegularWorkspace.value ? [SubscriptionModule.BACKUP_AND_RESTORE] : []),
      ...(isCanadaEnv ? [SubscriptionModule.SECURITY_AWARENESS_TRAINING] : []),
    ]);

    const isGlobalContext = computed(
      () => isGlobalAdmin && props.config.item.context === ManageWorkspacesContext.GLOBAL_WORKSPACES
    );

    const domainDisplayName = computed(() => {
      return isGlobalAdmin.value ? "coro.net" : workspaceStore.workspace.domain;
    });

    const stepsProgress = computed(() => {
      return getStepsProgress(steps.value);
    });

    const showScanUnprotectedUsers = computed(() => {
      if (type.value === WorkspaceType.CHILD) {
        return true;
      }
      return isChannelWorkspace.value;
    });

    const parentWorkspaceIdRules = computed(() => {
      const isChild = type.value === WorkspaceType.CHILD;
      const isChannel = type.value === WorkspaceType.CHANNEL;

      // If not a Child or Channel, no validation is required.
      if (!isChild && !isChannel) {
        return [];
      }

      // For Child workspaces - parentWorkspaceId is always required.
      if (isChild) {
        return [required()];
      }

      // Handling Channel logic
      if (isGlobalContext.value) {
        const isRestricted = isGlobalActionRestricted(
          MspRolePermissionScopes.MSP_PORTAL_SCOPE,
          MspPortalScopeSection.MSP_WORKSPACES,
          "createChannelWorkspace"
        );
        // If restricted (cannot create root), parent ID is required.
        // If not restricted (can create root), parent ID is optional.
        return isRestricted ? [required()] : [];
      }

      // Default Channel behavior: parentWorkspaceId is always required
      return [required()];
    });

    const onNextStepClick = async (callback: () => void) => {
      const validationResult = await form.value?.validate();
      if (validationResult?.valid) {
        callback();
      }
    };

    const onPrevStepClick = async (callback: () => void, currentStep: WorkspaceCreationSteps) => {
      await form.value?.resetValidation();
      // If on subscription we're returning to Subscription Rules, we should reset the selected state
      // of the subscription as the allowed modules and bundles can be changed
      if (showSubscriptionRulesStep.value && currentStep === WorkspaceCreationSteps.SUBSCRIPTION) {
        localValue.value.item.subscription = {
          bundles: [],
          modules: cloneDeep(defaultSubscriptionModulesConfig),
        };
      }
      callback();
    };

    const getLabels = async (search: string = "") => {
      try {
        const { data } = await labelsStore.searchLabels(
          search,
          localValue.value.item.parentWorkspaceId
        );
        labels.value = uniqBy([...localValue.value.item.labels, ...data], "id");
      } catch (e) {
        console.error(e);
        labels.value = [];
      }
    };

    const onLabelSearch = debounce(async (search: string) => {
      await getLabels(search);
    }, 500);

    const handleImageChange = (image: File) => {
      localValue.value!.item.image = image;
    };
    const handleEmailImageChange = (image: File) => {
      localValue.value!.item.emailImage = image;
    };

    const onParentWorkspaceSearchUpdate = debounce(function (searchString) {
      parentWorkspaceSearchPage.value = 0;
      parentWorkspaceSearchValue.value = searchString ?? "";
      if (!searchString && !localValue.value.item.parentWorkspaceId) {
        fetchParentWorkspaces();
      }
      if (searchString !== localValue.value.item.parentWorkspaceId) {
        fetchParentWorkspaces(searchString);
      }
    }, 500);

    const fetchParentWorkspaces = async (search: string = "", virtualScroll = false) => {
      if (props.disable) return;
      const permission: ParentWorkspaceSearchPermission =
        props.config.action === MspWorkspaceAction.CREATE_CHANNEL_WORKSPACE
          ? "CREATE_CHANNEL_WORKSPACE"
          : "CREATE_CHILD_WORKSPACE";
      parentWorkspaceInputLoading.value = true;
      const candidates = await mspStore.getWorkspaceCandidates(search, permission, {
        page: parentWorkspaceSearchPage.value,
        pageSize: 25,
      });
      hasMoreWorkspaces.value = parentWorkspaces.value.items.length < candidates.total;
      parentWorkspaces.value = virtualScroll
        ? {
            items: handleVirtualScrollData(
              parentWorkspaces.value.items,
              candidates.items,
              "workspaceId"
            ),
            total: candidates.total,
          }
        : candidates;
      parentWorkspaceInputLoading.value = false;
    };

    const fetchGlobalPolicies = async () => {
      if (props.disable) return;
      globalPoliciesLoading.value = true;

      const { data } = await mspGlobalPoliciesStore.searchGlobalPolicies("", {
        page: globalPoliciesSearchPage.value,
        pageSize: 25,
      });
      hasMorePolicies.value = globalPolicies.value.items.length < data.total;
      globalPolicies.value = {
        items: handleVirtualScrollData(globalPolicies.value.items, data.items, "id"),
        total: data.total,
      };
      globalPoliciesLoading.value = false;
    };

    const createWorkspace = async () => {
      // create workspace clicked is here to prevent double-click on create workspace
      // createWorkspaceInProgress can not be used because user can click on button twice
      // while he/she is waiting for response from checkInvitedAdminAlreadyMspAdmin
      createWorkspaceClicked.value = true;
      if (showParentWorkspaceIdInput.value && localValue.value.item.parentWorkspaceId) {
        adminExistsInOtherHierarchy.value = await mspStore.checkInvitedAdminAlreadyMspAdmin({
          parentWorkspaceId: localValue.value.item.parentWorkspaceId!,
          invitedAdmin: localValue.value.item.adminEmail!,
        });
      }
      try {
        const data = localValue.value;
        const createWorkspaceCallback = props.config.callback ?? mspStore.createWorkspace;
        await createWorkspaceCallback(data);
      } catch (e) {
        console.error(e);
      } finally {
        createWorkspaceClicked.value = false;
      }
    };

    const { onIntersect: onParentWorkspaceIntersect } = useInfiniteScroll(
      computed(() => parentWorkspaces.value.items),
      { hasMore: hasMoreWorkspaces },
      () => {
        parentWorkspaceSearchPage.value += 1;
        return fetchParentWorkspaces(parentWorkspaceSearchValue.value, true);
      }
    );

    const { onIntersect: onWorkspacePolicyIntersect } = useInfiniteScroll(
      computed(() => globalPolicies.value.items),
      { hasMore: hasMorePolicies },
      () => {
        globalPoliciesSearchPage.value += 1;
        return fetchGlobalPolicies();
      }
    );

    watch(
      () => localValue.value.item.limits?.numberOfProtectedUsers,
      (users) => {
        if (localValue.value.item.limits) {
          localValue.value.item.limits.numberOfProtectedDevices = users ? users * 3 : undefined;
        }
      }
    );

    watch(
      () => localValue.value?.item.branding.alias,
      (alias) => {
        localValue.value!.item.branding.noReplyEmail = alias ? `no-reply@${alias}.coro.net` : "";
      }
    );

    watch(
      () => localValue.value?.item.parentWorkspaceId,
      async (value) => {
        labels.value = [];
        getLabels();
        if (value) {
          const workspaceSubscriptionRules =
            await useMspStore().getWorkspaceSubscriptionRules(value);
          parentSubscriptionRules.value = structuredClone(workspaceSubscriptionRules);
          localValue.value.item.subscriptionRules = structuredClone(workspaceSubscriptionRules);
        } else {
          parentSubscriptionRules.value = undefined;
          // A fresh clone every time: `defaultSubscriptionRules` is a setup-scoped constant and
          // the rules card mutates the config it is handed, so assigning it by reference would
          // let the card empty the defaults for the rest of the dialog's life.
          localValue.value.item.subscriptionRules = cloneDeep(defaultSubscriptionRules);
        }
      }
    );

    watch(
      localValue,
      (value) => {
        emit("update:localValue", value);
      },
      { deep: true }
    );

    onMounted(async () => {
      mspStore.resetCreateWorkspaceLoaders();
      if (showLabelsField.value) {
        await getLabels();
      }
      if (showParentWorkspaceIdInput.value) {
        await fetchParentWorkspaces();
      }
      if (isChildWorkspace.value) {
        await fetchGlobalPolicies();
      }
    });

    return {
      globalPolicies,
      createWorkspaceInProgress,
      createWorkspaceClicked,
      createWorkspaceFailed,
      createWorkspaceCompleted,
      localValue,
      form,
      valid,
      limitProtectedUsersRules,
      domainRules,
      companyNameRules,
      displayNameRules,
      brandColorRules,
      adminEmailRules,
      supportEmailRules,
      aliasRules,
      filterKey,
      defaultLogoUrl,
      adminExistsInOtherHierarchy,
      showImageUploader,
      isChannelWorkspace,
      isChildWorkspace,
      isRegularWorkspace,
      hiddenModules,
      isGlobalContext,
      domainDisplayName,
      aliasHint,
      showScanUnprotectedUsers,
      handleImageChange,
      handleEmailImageChange,
      notificationLevels,
      onParentWorkspaceSearchUpdate,
      parentWorkspaceInputLoading,
      parentWorkspaces,
      fetchParentWorkspaces,
      required,
      showParentWorkspaceIdInput,
      maxItems,
      currentStep,
      steps,
      workspaceCreationSteps: WorkspaceCreationSteps,
      bundles: SubscriptionBundle,
      moduleIncludedInBundle,
      addonIncludedInBundle,
      showPreviousStepBtn,
      showCompleteBtn,
      getCurrentStepNumber,
      stepsProgress,
      onNextStepClick,
      onPrevStepClick,
      isSocAddon,
      hasSocAddonInModule,
      isCanadaEnv,
      createWorkspace,
      showSummaryModulesAndAddons,
      subscriptionSummaryType: SubscriptionSummaryType,
      subscriptionAddons: SubscriptionAddon,
      isGlobalAdmin,
      parentWorkspaceIdRules,
      WorkspaceLocale,
      getNotificationLevelText,
      atLeastOneModuleNotSelected,
      labels,
      onLabelSearch,
      showLabelsField,
      onParentWorkspaceIntersect,
      onWorkspacePolicyIntersect,
      globalPoliciesLoading,
      parentSubscriptionRules,
      bundleLabels,
      moduleLabels,
    };
  },
});
</script>

<style lang="scss" scoped>
.progress-container {
  height: 300px;
}

.v-stepper.v-sheet {
  box-shadow: unset !important;
  background-color: transparent;
}

.v-stepper-header {
  box-shadow: unset !important;
}

.v-stepper-window {
  margin: unset !important;
}

.stepper-step__wrapper {
  padding-bottom: 20px;
  padding-top: 20px;

  .v-card {
    border-radius: 8px !important;
  }
}

.dialog-content {
  min-width: 650px;
  max-width: 1150px;
  width: 100%;
  margin-right: 30px;
  margin-left: 30px;
}

.scroll-area {
  height: calc(100vh - 160px);
  overflow: auto;
}

.v-switch {
  .v-label {
    opacity: 1 !important;
  }
}

.color-preview {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

.notification-label {
  width: 500px;
}

.row-gap {
  gap: 23px;
}

:deep(*) {
  .v-chip__close {
    .icon-rules-close:before {
      color: rgb(var(--v-theme-primary));
    }
  }

  .v-btn-group--density-default.v-btn-group {
    height: unset !important;
  }

  .bundle-selector {
    background-color: white;
  }

  .v-btn-group {
    width: 100%;

    .v-btn {
      width: 50%;
    }
  }
}

.info-block--large {
  min-height: 68px;
}

.summary-item {
  border: 1px solid rgb(var(--v-theme-indigo-faint)) !important;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
}

.progress-bar__content {
  width: 200px;
}

.included-in-bundle-block {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  text-transform: uppercase;
  font-weight: 600;
}
</style>
